import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  Message as MessageIcon,
  Group as UsersIcon,
  Logout as LogoutIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CalendarMonth as CalendarIcon,
  ContactMail as ContactsIcon,
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { useAuth, PERMISSIONS } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/helpers';

const menuItems = [
  { path: '/', label: 'Dashboard', icon: <DashboardIcon fontSize="small" />, permission: PERMISSIONS.DASHBOARD_VIEW },
  { path: '/leads', label: 'Leads', icon: <PersonAddIcon fontSize="small" />, permission: PERMISSIONS.LEADS_VIEW },
  { path: '/calendar', label: 'Calendar', icon: <CalendarIcon fontSize="small" />, permission: PERMISSIONS.LEADS_VIEW },
  { path: '/contacts', label: 'Contacts', icon: <ContactsIcon fontSize="small" />, permission: PERMISSIONS.CONTACTS_VIEW },
  { path: '/clients', label: 'Clients', icon: <PeopleIcon fontSize="small" />, permission: PERMISSIONS.CLIENTS_VIEW },
  { path: '/billing', label: 'Billing', icon: <ReceiptIcon fontSize="small" />, permission: PERMISSIONS.BILLING_VIEW },
  { path: '/analytics', label: 'Analytics', icon: <AssessmentIcon fontSize="small" />, permission: PERMISSIONS.ANALYTICS_VIEW },
  { path: '/templates', label: 'DM Templates', icon: <MessageIcon fontSize="small" />, permission: PERMISSIONS.TEMPLATES_VIEW },
  { path: '/settings', label: 'Settings', icon: <SettingsIcon fontSize="small" />, permission: PERMISSIONS.SETTINGS_VIEW },
  { path: '/users', label: 'Users', icon: <UsersIcon fontSize="small" />, permission: PERMISSIONS.USERS_VIEW },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { dashboardData, settings, showSnackbar } = useApp();
  const { user, hasPermission, logout, changePassword } = useAuth();

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const currentMRR = dashboardData?.mrr?.current || 0;
  const mrrGoal = settings?.mrrGoal || 2000;
  const mrrProgress = Math.min((currentMRR / mrrGoal) * 100, 100);

  const activeClients = dashboardData?.clients?.active || 0;
  const clientGoal = settings?.clientGoal || 50;

  // Filter menu items based on user permissions
  const visibleMenuItems = menuItems.filter((item) => hasPermission(item.permission));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleOpenPasswordDialog = () => {
    setPasswordDialogOpen(true);
    setPasswordError('');
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordError('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const handleSubmitPasswordChange = async () => {
    setPasswordError('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      showSnackbar('Password changed successfully', 'success');
      handleClosePasswordDialog();
    } catch (error) {
      setPasswordError(error.response?.data?.message || 'Failed to change password');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo/Brand */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: 32,
            height: 32,
            fontSize: '0.875rem',
            fontWeight: 700,
          }}
        >
          W
        </Avatar>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
            WebFlow
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
            CRM
          </Typography>
        </Box>
      </Box>

      {/* MRR Progress Card */}
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'primary.main',
            borderRadius: 1.5,
            color: 'white',
          }}
        >
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Monthly Revenue
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {formatCurrency(currentMRR)}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={mrrProgress}
            sx={{
              height: 4,
              borderRadius: 2,
              mt: 0.5,
              bgcolor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'white',
              },
            }}
          />
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.65rem', mt: 0.25, display: 'block' }}>
            {mrrProgress.toFixed(0)}% of {formatCurrency(mrrGoal)} goal
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <List sx={{ px: 1.5, flexGrow: 1, py: 0 }}>
        {visibleMenuItems.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));

          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                selected={isActive}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 1,
                  py: 0.75,
                  px: 1.5,
                  minHeight: 36,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 28,
                    color: isActive ? 'white' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.8125rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* Quick Stats */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Active Clients
          </Typography>
          <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
            {activeClients} / {clientGoal}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* User Info & Logout */}
      <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          sx={{
            bgcolor: user?.role === 'admin' ? 'primary.main' : 'grey.400',
            width: 28,
            height: 28,
            fontSize: '0.75rem',
          }}
        >
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {user?.name || 'User'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {user?.role === 'admin' ? 'Admin' : 'User'}
          </Typography>
        </Box>
        <Tooltip title="Change Password">
          <IconButton size="small" onClick={handleOpenPasswordDialog} sx={{ color: 'text.secondary' }}>
            <LockIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Logout">
          <IconButton size="small" onClick={handleLogout} sx={{ color: 'text.secondary' }}>
            <LogoutIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {passwordError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Current Password"
            name="currentPassword"
            type={showCurrentPassword ? 'text' : 'password'}
            value={passwordForm.currentPassword}
            onChange={handlePasswordChange}
            sx={{ mt: 1, mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                    size="small"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="New Password"
            name="newPassword"
            type={showNewPassword ? 'text' : 'password'}
            value={passwordForm.newPassword}
            onChange={handlePasswordChange}
            helperText="At least 6 characters"
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    size="small"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirm New Password"
            name="confirmPassword"
            type={showNewPassword ? 'text' : 'password'}
            value={passwordForm.confirmPassword}
            onChange={handlePasswordChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitPasswordChange}>
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sidebar;
